import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight, AlertTriangle, MapPin, Layers, Package, Check } from 'lucide-react';
import { inventoriesAPI, locationsAPI, zonesAPI } from '../../services/garageApi';

const ManageStockModal = ({ isOpen, onClose, productIds = [], productLabel = '', productType = 'tire', onStockMoved }) => {
  const [inventories, setInventories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [targetZones, setTargetZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasNonSystemZones, setHasNonSystemZones] = useState(null);

  const [form, setForm] = useState({
    source_inventory_id: '',
    target_location_id: '',
    target_zone_id: '',
    quantity: '',
  });

  // Load inventory distribution for these products
  const loadInventories = useCallback(async () => {
    if (!productIds.length) return;
    setLoading(true);
    setError('');
    try {
      const allInventories = [];
      for (const productId of productIds) {
        const response = await inventoriesAPI.getByProduct(productType, productId);
        const items = Array.isArray(response.data) ? response.data : [];
        allInventories.push(...items);
      }
      setInventories(allInventories);
    } catch (err) {
      console.error('Error loading inventories:', err);
      setError('Failed to load stock distribution. ' + (err?.response?.data?.message || err?.message || ''));
    } finally {
      setLoading(false);
    }
  }, [productIds, productType]);

  // Load locations for target dropdown and check for non-system zones
  const loadLocations = useCallback(async () => {
    try {
      const response = await locationsAPI.getAll({ per_page: 100 });
      const locs = response?.data?.data?.data || response?.data?.data || response?.data || [];
      const locArray = Array.isArray(locs) ? locs : [];
      setLocations(locArray);

      // Check if any non-system zones exist across all locations
      let foundNonSystemZone = false;
      for (const loc of locArray) {
        try {
          const zoneResp = await zonesAPI.getByLocation(loc.id);
          const zones = zoneResp?.data?.data?.data || zoneResp?.data?.data || zoneResp?.data || [];
          const nonSystem = (Array.isArray(zones) ? zones : []).filter(z => !z.is_system);
          if (nonSystem.length > 0) {
            foundNonSystemZone = true;
            break;
          }
        } catch (e) { /* continue */ }
      }
      setHasNonSystemZones(foundNonSystemZone);
    } catch (err) {
      console.error('Error loading locations:', err);
      setHasNonSystemZones(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadInventories();
      loadLocations();
      setForm({ source_inventory_id: '', target_location_id: '', target_zone_id: '', quantity: '' });
      setSuccess('');
      setError('');
    }
  }, [isOpen, loadInventories, loadLocations]);

  // Load zones when target location changes
  useEffect(() => {
    if (!form.target_location_id) {
      setTargetZones([]);
      return;
    }
    const loadZones = async () => {
      try {
        const response = await zonesAPI.getByLocation(form.target_location_id);
        const zones = response?.data?.data?.data || response?.data?.data || response?.data || [];
        // Filter out system (Unallocated) zones
        const filteredZones = (Array.isArray(zones) ? zones : []).filter(z => !z.is_system);
        setTargetZones(filteredZones);
      } catch (err) {
        console.error('Error loading zones:', err);
        setTargetZones([]);
      }
    };
    loadZones();
  }, [form.target_location_id]);

  const sourceInventory = inventories.find(inv => inv.id === form.source_inventory_id);
  const maxQuantity = sourceInventory ? sourceInventory.quantity : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.source_inventory_id || !form.target_location_id || !form.target_zone_id || !form.quantity) {
      setError('All fields are required.');
      return;
    }

    const qty = parseInt(form.quantity);
    if (qty < 1 || qty > maxQuantity) {
      setError(`Quantity must be between 1 and ${maxQuantity}.`);
      return;
    }

    setSubmitting(true);
    try {
      await inventoriesAPI.moveStock({
        source_inventory_id: form.source_inventory_id,
        target_location_id: parseInt(form.target_location_id),
        target_zone_id: parseInt(form.target_zone_id),
        quantity: qty,
      });

      setSuccess(`Successfully moved ${qty} units.`);
      setForm({ source_inventory_id: '', target_location_id: '', target_zone_id: '', quantity: '' });

      // Refresh inventory data
      await loadInventories();

      if (onStockMoved) onStockMoved();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to move stock.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Manage Stock</h2>
            {productLabel && (
              <p className="text-sm text-gray-500 mt-1">{productLabel}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
              <Check className="w-4 h-4" />
              {success}
            </div>
          )}

          {/* Current Distribution Table */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Current Distribution</h3>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : inventories.length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Zone</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {inventories.map((inv) => {
                      const isUnallocated = inv.zone?.is_system;
                      return (
                        <tr key={inv.id} className={isUnallocated ? 'bg-yellow-50' : ''}>
                          <td className="px-4 py-2.5 flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            {inv.location?.name || 'Unknown'}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <Layers className="w-3.5 h-3.5 text-gray-400" />
                              {inv.zone?.name || 'No Zone'}
                              {isUnallocated && (
                                <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium">{inv.quantity}</td>
                          <td className="px-4 py-2.5">
                            {isUnallocated ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                <AlertTriangle className="w-3 h-3" />
                                Needs Allocation
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <Check className="w-3 h-3" />
                                Allocated
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                No inventory records found.
              </div>
            )}
          </div>

          {/* Move Stock Form */}
          {inventories.some(inv => inv.quantity > 0) && (
            <div className="border-t pt-6">
              {hasNonSystemZones === false ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    <h3 className="text-sm font-semibold text-yellow-800">No zones configured</h3>
                  </div>
                  <p className="text-sm text-yellow-700">
                    To move stock out of the Unallocated zone, you need to create at least one zone within a location. Go to Settings &gt; Locations to add zones.
                  </p>
                </div>
              ) : (
              <>
              <h3 className="text-sm font-medium text-gray-700 mb-4">Move Stock</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Source */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">From (Source)</label>
                  <select
                    value={form.source_inventory_id}
                    onChange={(e) => setForm(prev => ({ ...prev, source_inventory_id: e.target.value, quantity: '' }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select source...</option>
                    {inventories.filter(inv => inv.quantity > 0).map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {inv.location?.name || 'Unknown'} / {inv.zone?.name || 'No Zone'} ({inv.quantity} units)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>

                {/* Target Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">To Location</label>
                    <select
                      value={form.target_location_id}
                      onChange={(e) => setForm(prev => ({ ...prev, target_location_id: e.target.value, target_zone_id: '' }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select location...</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">To Zone</label>
                    <select
                      value={form.target_zone_id}
                      onChange={(e) => setForm(prev => ({ ...prev, target_zone_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!form.target_location_id || targetZones.length === 0}
                    >
                      <option value="">
                        {!form.target_location_id ? 'Select location first' :
                         targetZones.length === 0 ? 'No zones available' : 'Select zone...'}
                      </option>
                      {targetZones.map(zone => (
                        <option key={zone.id} value={zone.id}>{zone.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Quantity to Move
                    {maxQuantity > 0 && (
                      <span className="text-gray-400 ml-1">(max: {maxQuantity})</span>
                    )}
                  </label>
                  <input
                    type="number"
                    value={form.quantity}
                    onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value }))}
                    min="1"
                    max={maxQuantity}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter quantity..."
                    disabled={!form.source_inventory_id}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || !form.source_inventory_id || !form.target_location_id || !form.target_zone_id || !form.quantity}
                  className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Moving...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4" />
                      Move Stock
                    </>
                  )}
                </button>
              </form>
              </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ManageStockModal;
